/**
 * 听牌 / 待牌计算
 *
 * 判断手牌是否听牌，以及听哪些牌。
 * 相比 shanten.ts 的 isTenpai()，本模块提供更丰富的信息：
 *   · 听的牌列表（含剩余枚数）
 *   · 听牌型（两面/双碰/坎张/边张/单骑）
 *   · 各听牌的分解方式（用于算番）
 */

import {
  Counts,
  COPIES_PER_TILE,
  NUM_TILE_TYPES,
  allTileIndices,
  cloneCounts,
  countsTotal,
  indexToName,
} from './tile.js';
import { decomposeAll, type Decomposition } from './agari.js';

// ════════════════════════════════════════════════════════════════
// 类型
// ════════════════════════════════════════════════════════════════

/** 听牌型 */
export type WaitType =
  | 'ryanmen' // 两面
  | 'kanchan' // 坎张
  | 'penchan' // 边张
  | 'shanpon' // 双碰
  | 'tanki' // 单骑
  | 'kokushi'; // 国士

/** 单张待牌信息 */
export interface WaitTile {
  /** 牌索引 */
  readonly tile: number;
  /** 牌名 */
  readonly name: string;
  /** 剩余枚数（4 - 已见数） */
  readonly remaining: number;
  /** 和的牌型 */
  readonly waitTypes: readonly WaitType[];
  /** 用这张牌和了时的分解方式 */
  readonly decompositions: readonly Decomposition[];
}

/** 听牌分析结果 */
export interface WaitAnalysis {
  /** 是否听牌 */
  readonly isTenpai: boolean;
  /** 待牌列表 */
  readonly waits: readonly WaitTile[];
  /** 待牌总数（考虑剩余枚数） */
  readonly totalRemaining: number;
  /** 总听张数（种类数） */
  readonly kinds: number;
}

// ════════════════════════════════════════════════════════════════
// 主入口
// ════════════════════════════════════════════════════════════════

/**
 * 分析手牌的听牌情况
 *
 * @param counts 手牌计数（13 张 + 副露×3 = 13）
 * @param melds 副露面子（可选，仅用于分解展示）
 * @param visible 已见的牌（用于计算剩余枚数，可选）
 */
export function analyzeWaits(
  counts: Counts,
  melds: Parameters<typeof decomposeAll>[1] = [],
  visible?: Counts,
): WaitAnalysis {
  const waits: WaitTile[] = [];
  const meldCount = melds.length;
  const total = countsTotal(counts) + meldCount * 3;

  // 听牌时手牌应为 13 张（或 13 - meldCount*3）
  if (total !== 13) {
    return { isTenpai: false, waits: [], totalRemaining: 0, kinds: 0 };
  }

  for (const tile of allTileIndices()) {
    // 该牌已用完 4 张 → 不可能和
    if (counts[tile] >= COPIES_PER_TILE) continue;

    const test = cloneCounts(counts);
    test[tile]++;

    // 用这张牌能否和了
    const ds = decomposeAll(test, melds);
    if (ds.length === 0) continue;

    // 判断听牌型
    const waitTypes = determineWaitTypes(counts, tile, ds);

    // 剩余枚数
    const seen = visible ? visible[tile] : counts[tile];
    const remaining = COPIES_PER_TILE - seen;

    waits.push({
      tile,
      name: indexToName(tile),
      remaining,
      waitTypes,
      decompositions: ds,
    });
  }

  const totalRemaining = waits.reduce((s, w) => s + w.remaining, 0);
  return {
    isTenpai: waits.length > 0,
    waits,
    totalRemaining,
    kinds: waits.length,
  };
}

/** 简化版：只要待牌索引列表 */
export function waitingTiles(counts: Counts, meldCount = 0): number[] {
  const r = analyzeWaits(
    counts,
    Array.from({ length: meldCount }, () => ({
      kind: 'shuntsu' as const,
      start: 0,
      open: true,
      names: [] as string[],
    })),
  );
  return r.waits.map((w) => w.tile);
}

// ════════════════════════════════════════════════════════════════
// 听牌型判定
// ════════════════════════════════════════════════════════════════

/**
 * 判断和了牌的听牌型
 *
 * 判定规则（基于分解结果 + 和牌前的手牌张数）：
 *   · 单骑 tanki   —— 和的牌单独作雀头（手牌中原本 1 张）
 *   · 双碰 shanpon —— 和的牌凑成刻子的第 3 张（手牌中原本 2 张）
 *   · 坎张 kanchan —— 和的牌夹在顺子中间（如 1_3 听 2）
 *   · 边张 penchan —— 和的牌是 7，且顺子是 789（原手牌 89）／和了牌是 3 且顺子 123（原手牌 12）
 *   · 两面 ryanmen —— 其余顺子端点的情形
 *
 * 备注：副露（open）的面子不含和了牌，跳过。
 */
function determineWaitTypes(
  before: Counts,
  winTile: number,
  decompositions: readonly Decomposition[],
): WaitType[] {
  const types = new Set<WaitType>();
  const countBefore = before[winTile];

  for (const d of decompositions) {
    if (d.form === 'kokushi') {
      types.add('kokushi');
      continue;
    }
    if (d.form === 'chiitoitsu') {
      types.add('tanki');
      continue;
    }

    // 单骑：和的牌作雀头，且手牌中原本只 1 张
    if (d.pair === winTile && countBefore === 1) {
      types.add('tanki');
      continue;
    }

    for (const m of d.mentsu) {
      if (m.open) continue;

      if (m.kind === 'koutsu' || m.kind === 'kantsu') {
        // 刻子/杠子：和的牌凑成第 3（4）张 → 双碰
        if (m.start === winTile && countBefore >= 2) {
          types.add('shanpon');
        }
        continue;
      }

      // 顺子：判断和了牌在顺子里的位置
      if (m.kind === 'shuntsu') {
        const startIdx = m.start;
        const endIdx = startIdx + 2;
        const startFace = (startIdx % 9) + 1; // 顺子最小牌的牌面（1-9）

        if (winTile === startIdx + 1) {
          types.add('kanchan'); // 夹在中间
        } else if (winTile === startIdx) {
          // 和了牌是最小牌：原手牌是 (startIdx+1, startIdx+2) 两张连续
          // 边张仅当这对牌是 8,9（即 startFace=7 时顺子为 7-8-9，原手牌 89 听 7）
          types.add(startFace === 7 ? 'penchan' : 'ryanmen');
        } else if (winTile === endIdx) {
          // 和了牌是最大牌：原手牌是 (startIdx, startIdx+1)
          // 边张仅当这对牌是 1,2（即 startFace=1 时顺子为 1-2-3，原手牌 12 听 3）
          types.add(startFace === 1 ? 'penchan' : 'ryanmen');
        }
      }
    }
  }

  return [...types];
}

// ════════════════════════════════════════════════════════════════
// 待摸牌（进张）计算 —— 一向听时列出所有能进听牌的牌
// ════════════════════════════════════════════════════════════════

/** 待摸牌（进张）信息 */
export interface DrawTile {
  readonly tile: number;
  readonly name: string;
  readonly remaining: number;
}

/** 进张分析结果 */
export interface DrawAnalysis {
  /** 当前向听数 */
  readonly shanten: number;
  /** 进张列表（摸到后向听数降低） */
  readonly draws: readonly DrawTile[];
  /** 进张总数（考虑剩余枚数） */
  readonly totalRemaining: number;
}

/**
 * 分析进张（能降低向听数的牌）
 *
 * @param counts 手牌计数
 * @param meldCount 副露数
 * @param shantenFn 向听数函数（避免循环依赖，由调用方传入）
 * @param visible 已见牌（可选）
 */
export function analyzeDraws(
  counts: Counts,
  meldCount: number,
  shantenFn: (counts: Counts, meldCount: number) => number,
  visible?: Counts,
): DrawAnalysis {
  const current = shantenFn(counts, meldCount);
  const draws: DrawTile[] = [];
  const total = countsTotal(counts) + meldCount * 3;

  // 只有 13 张时才能分析进张
  if (total !== 13 || current < 0) {
    return { shanten: current, draws: [], totalRemaining: 0 };
  }

  for (const tile of allTileIndices()) {
    if (counts[tile] >= COPIES_PER_TILE) continue;
    const test = cloneCounts(counts);
    test[tile]++;
    const after = shantenFn(test, meldCount);
    if (after < current) {
      const seen = visible ? visible[tile] : counts[tile];
      draws.push({
        tile,
        name: indexToName(tile),
        remaining: COPIES_PER_TILE - seen,
      });
    }
  }

  return {
    shanten: current,
    draws,
    totalRemaining: draws.reduce((s, d) => s + d.remaining, 0),
  };
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 待牌列表 → 可读字符串（如 "3s(4) 6s(4)"） */
export function formatWaits(waits: readonly WaitTile[]): string {
  return waits.map((w) => `${w.name}(${w.remaining})`).join(' ');
}

/** 听牌型 → 中文名 */
export function waitTypeName(t: WaitType): string {
  const names: Record<WaitType, string> = {
    ryanmen: '两面',
    kanchan: '坎张',
    penchan: '边张',
    shanpon: '双碰',
    tanki: '单骑',
    kokushi: '国士',
  };
  return names[t];
}

/** 听牌型统计（如 "两面×2 单骑×1"） */
export function summarizeWaitTypes(waits: readonly WaitTile[]): string {
  const counter = new Map<WaitType, number>();
  for (const w of waits) {
    for (const t of w.waitTypes) {
      counter.set(t, (counter.get(t) ?? 0) + 1);
    }
  }
  return [...counter.entries()]
    .map(([t, n]) => `${waitTypeName(t)}×${n}`)
    .join(' ');
}

/** 未使用的导入占位（保持 NUM_TILE_TYPES 引用避免 lint 警告） */
export const TILE_TYPE_COUNT = NUM_TILE_TYPES;
